import Highcharts, { Options, Tooltip } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { parseInt } from 'lodash';
import { useEffect, useState } from 'react';
import DarkTheme from 'highcharts/themes/high-contrast-dark';
import LightTheme from 'highcharts/themes/high-contrast-light';
import { useUi } from 'hooks/useUi';

interface RankingGraphProps {
    ranks: string[];
    supplierRank: number;
}

export const RankingGraph = ({ ranks, supplierRank }: RankingGraphProps) => {
    const { ui } = useUi();

    const [options, setOptions] = useState<Options>({
        credits: {
            enabled: false,
        },
        chart: {
            style: {
                margin: 'auto',
            },
        },
        title: {
            text: '',
        },
        legend: {
            enabled: false,
        },
        plotOptions: {
            series: {
                stacking: 'normal',
            },
            columnrange: {
                grouping: false,
            },
        },
        xAxis: {
            labels: {
                enabled: false,
            },
        },
    });

    // Set graph theme based on user's color theme.
    useEffect(() => {
        if (ui.theme === 'dark') DarkTheme(Highcharts);
        else LightTheme(Highcharts);
    }, [ui.theme]);

    useEffect(() => {
        if (ranks && supplierRank) {
            setOptions((prevOpts) => {
                return {
                    ...prevOpts,
                    yAxis: {
                        reversed: true,
                        labels: {
                            enabled: false,
                        },
                        title: {
                            style: {
                                display: 'none',
                            },
                        },
                        plotLines: [
                            {
                                color: 'orange',
                                dashStyle: 'Solid',
                                value: supplierRank,
                                width: 2,
                                zIndex: 5,
                                label: {
                                    y: -7,
                                    text:
                                        'You Rank #' +
                                        supplierRank +
                                        ' out of ' +
                                        ranks.length,
                                    style: {
                                        color: 'black',
                                        fontWeight: 'bold',
                                    },
                                },
                            },
                            {
                                color: 'orange',
                                dashStyle: 'Solid',
                                value: supplierRank,
                                width: 2,
                                zIndex: 5,
                                label: {
                                    text:
                                        parseInt(ranks[supplierRank]) +
                                        '% Avg Delivery Size',
                                    y: 16,
                                    style: {
                                        color: 'black',
                                        fontWeight: 'bold',
                                    },
                                },
                            },
                            {
                                color: 'grey',
                                dashStyle: 'Solid',
                                value: 0,
                                width: 2,
                                zIndex: 5,
                                label: {
                                    align: 'right',
                                    text:
                                        parseInt(ranks[1]) +
                                        '% Avg Delivery Size',
                                    y: 16,
                                    x: -10,
                                    style: {
                                        color: 'black',
                                        fontWeight: 'bold',
                                    },
                                },
                            },
                            {
                                color: 'grey',
                                dashStyle: 'Solid',
                                value: ranks.length - 1,
                                width: 2,
                                zIndex: 5,
                                label: {
                                    align: 'right',
                                    text:
                                        parseInt(ranks[ranks.length - 1]) +
                                        '% Avg Delivery Size',
                                    y: 16,
                                    x: -10,
                                    style: {
                                        color: 'black',
                                        fontWeight: 'bold',
                                    },
                                },
                            },
                        ],
                    },
                    series: [
                        {
                            name: 'below',
                            data: [ranks.length - supplierRank],
                            color: '#BBDEFB',
                            type: 'bar',
                            pointWidth: 100,
                            label: {
                                enabled: false,
                            },
                            title: {
                                enabled: false,
                            },
                            showInLegend: false,
                        },
                        {
                            name: 'above',
                            type: 'bar',
                            data: [supplierRank],
                            color: '#1E88E5',
                            pointWidth: 100,
                            label: {
                                enabled: false,
                            },
                            title: {
                                enabled: false,
                            },
                            showInLegend: false,
                        },
                    ],
                    tooltip: {
                        valueDecimals: 0,
                        shared: false,
                        formatter: (tooltip: Tooltip) => {
                            if (tooltip?.chart?.hoverSeries?.name === 'below') {
                                return `There are ${
                                    ranks.length - supplierRank
                                } fuel suppliers ranked below you.`;
                            }

                            return `There are ${
                                supplierRank - 1
                            } fuel suppliers ranked above you.`;
                        },
                    },
                };
            });
        }
    }, [ranks, supplierRank]);

    return <HighchartsReact options={options} highcharts={Highcharts} />;
};
